<script setup>
import { useId } from 'vue';

import { useTheme } from '../composables/useTheme';
import { HrCheckbox } from '../primitives';
import HrIconHintBtn from './HrIconHintBtn.vue';

/**
 * Setting checkbox — a checkbox whose effect needs more than its label can carry: one sentence of
 * supporting text under the label, a longer explanation behind a help button beside it, or both.
 *
 * `HrSettingSwitch` is the sibling for a setting that applies the moment it is flipped, and it sends
 * save-committed forms to a checkbox — which then had nowhere to put the same supporting text. This
 * is that missing half.
 *
 * THE HELP BUTTON IS A SIBLING OF THE CHECKBOX, NOT A CHILD OF IT. Inside the label it sits within
 * the checkbox's own hover target, so pointing at the button lights up the checkbox's state layer as
 * if the box itself were about to be clicked. Keeping it outside fixes that by construction rather
 * than by suppressing the state layer, and it means the button needs no click guard to avoid
 * toggling the value.
 *
 * THE BUTTON IS ALWAYS VISIBLE. Faded in on hover, it kept a column of settings quiet, but a reader
 * who never pointed at the row never learned there was a hint to read.
 *
 * Attrs are routed to the checkbox rather than the wrapper, so a `data-e2e` hook lands on the control
 * a test means to click rather than on a div containing it.
 *
 * @status recommended
 * @group forms-input
 * @useWhen A checkbox setting whose consequence is not obvious from its label
 * @avoidWhen The setting applies immediately rather than on save — use HrSettingSwitch
 * @avoidWhen The label says everything — use HrCheckbox
 * @related HrSettingSwitch
 * @related HrCheckbox
 * @related HrIconHintBtn
 * @a11y The hint opens in a dialog rather than a tooltip, so it is reachable by keyboard and touch.
 */
defineOptions({ name: 'HrSettingCheckbox', inheritAttrs: false });

defineProps({
  /** Current value. */
  modelValue: {
    type: null,
    default: false,
  },
  /** Label text shown beside the checkbox. */
  label: {
    type: String,
    required: true,
  },
  /**
   * Supporting text below the label: one sentence the reader needs to decide. A longer or
   * occasional explanation goes in `hint`, never in a tooltip.
   */
  description: {
    type: String,
    default: null,
  },
  /** Longer or occasional explanation behind a help button. Without it no button renders. */
  hint: {
    type: String,
    default: null,
  },
  /**
   * Value emitted when checked. Set alongside `falseValue` where the label reads the opposite way
   * round from the field it writes, so the control can say "show" while the field records "hide".
   */
  trueValue: {
    type: null,
    default: true,
  },
  /** Value emitted when unchecked. */
  falseValue: {
    type: null,
    default: false,
  },
  /** Prevents interaction and dims the checkbox. The hint stays available. */
  disable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Emitted when the checked state changes; payload is the new value. */
  'update:modelValue': null,
});

const descriptionId = useId();
const { hasTheme } = useTheme();
</script>

<template>
  <div :class="['hr-setting-checkbox', hasTheme && 'hr-setting-checkbox--themed']">
    <div class="hr-setting-checkbox__row">
      <hr-checkbox
        :model-value="modelValue"
        :label="label"
        :true-value="trueValue"
        :false-value="falseValue"
        :disable="disable"
        :aria-describedby="description ? descriptionId : undefined"
        v-bind="$attrs"
        @update:model-value="emit('update:modelValue', $event)"
      />
      <hr-icon-hint-btn
        v-if="hint"
        :hint="hint"
      />
    </div>
    <span
      v-if="description"
      :id="descriptionId"
      class="hr-setting-checkbox__description"
      :class="hasTheme ? 'hr-body-medium' : undefined"
    >{{ description }}</span>
  </div>
</template>

<style lang="scss" scoped>
.hr-setting-checkbox__row {
  display: flex;
  align-items: center;
  gap: 8px;
}

// The checkbox's 40dp state layer plus its 8dp label gap, so the text starts under the label.
.hr-setting-checkbox__description {
  display: block;
  padding-inline-start: 48px;
}

.hr-setting-checkbox--themed .hr-setting-checkbox__description {
  color: var(--app-on-surface-variant);
}
</style>
